import { useSuspenseQuery } from "@apollo/client";
import InsightsIcon from "@mui/icons-material/Insights";
import { Alert, Box, GridProps, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { DataGrid } from "components/DataGrid";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import { OutstandingReviewer } from "generated-graphql/graphql";
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useChemicalOverviewState } from "../ChemicalOverviewContext";

const OUTSTANDING_REVIEWS_QUERY = gql(`
  query OutstandingReviews($tenantId: ID!, $filter: ChemicalOverviewFilter) {
    outstandingReviews(tenantId: $tenantId, filter: $filter) {
      reviewers {
        userId
        first
        last
        email
        reportCount
      }
      reportsWithoutReviewerCount
    }
  }
`);

const columns: GridColDef[] = [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    valueGetter: (_, row) => `${row.first} ${row.last}`,
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
  },
  {
    field: "reportCount",
    headerName: "Number of Reports",
    type: "number",
    flex: 1,
  },
];

export function OutstandingReviewsTile(props: GridProps) {
  return (
    <DashboardTile {...props}>
      <OutstandingReviewsContent />
    </DashboardTile>
  );
}

function OutstandingReviewsContent() {
  const theme = useTheme();
  const { tenantId } = useParams();
  const { chemicalOverviewState, drillDown } = useChemicalOverviewState();

  const { data } = useSuspenseQuery(OUTSTANDING_REVIEWS_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: {
        assignedToMe: chemicalOverviewState.deferredFilters.assignedToMe,
        tagNames: chemicalOverviewState.deferredFilters.facilityTags,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const drillDownToFacilitiesWithReviewer = useCallback(
    (search: string) => {
      drillDown((f) => ({
        ...f,
        reviewer: search,
        organizationStatus: "IN_REVIEW",
      }));
    },
    [drillDown]
  );

  const { reviewers, reportsWithoutReviewerCount } =
    data?.outstandingReviews ?? {
      reviewers: [],
      reportsWithoutReviewerCount: 0,
    };

  return (
    <>
      <DashboardTile.Header
        title="Outstanding Reviews"
        infoTooltip={
          <Typography variant="caption">
            <Typography component="span" variant="caption" fontWeight={800}>
              Outstanding Reviews
            </Typography>{" "}
            displays all incomplete reviews for the current reporting year for
            reports that have a status of In Review, grouped by reviewer.
          </Typography>
        }
      />
      <DashboardTile.ContentArea>
        <DataGrid
          rows={reviewers}
          columns={columns}
          getRowId={(row) => row.userId}
          pagination={true}
          pageSizeOptions={[5]}
          initialState={{
            pagination: { paginationModel: { pageSize: 5 } },
            sorting: {
              sortModel: [
                { field: "reportCount", sort: "desc" },
                { field: "name", sort: "asc" },
              ],
            },
          }}
          autoHeight
          onRowClick={(item: GridRowParams<OutstandingReviewer>) => {
            drillDownToFacilitiesWithReviewer(
              item.row.first && item.row.last
                ? getFullName(item.row)
                : item.row.email
            );
          }}
        />
      </DashboardTile.ContentArea>
      {reportsWithoutReviewerCount > 0 && (
        <>
          <DashboardTile.Footer>
            <Box
              sx={{
                cursor: "pointer",
                "&:hover": {
                  "& .MuiAlert-icon": {
                    color: theme.palette.info.main,
                  },
                  "& .MuiTypography-root": {
                    color: theme.palette.info.main,
                  },
                },
              }}
              onClick={() => drillDownToFacilitiesWithReviewer("false")}
            >
              <Alert severity="info" icon={<InsightsIcon />}>
                <Typography>
                  <Typography component="span" fontWeight="800">
                    {reportsWithoutReviewerCount}
                  </Typography>{" "}
                  of your reports{" "}
                  {reportsWithoutReviewerCount === 1 ? "has" : "have"} no
                  reviewer assigned.
                </Typography>
              </Alert>
            </Box>
          </DashboardTile.Footer>
        </>
      )}
    </>
  );
}
